export type DieConfiguration = {
  dieType: string;
  vertices: Array<Array<number>>;
  faces: Array<Array<number>>;
  scaleFactor: number;
  values: number;
  mass: number;
  meshSizeMultiplier: number;
  invertUpside: boolean;
  numberValues: Record<number, number>;
  baseHeight: number;
};

export const DieTypes = {
  d4: "d4",
  d6: "d6",
  d8: "d8",
  d10: "d10",
  d12: "d12",
  d20: "d20",
  d100: "d100",
} as const;

const d4: DieConfiguration = {
  dieType: DieTypes.d4,
  vertices: [
    [1, 1, 1],
    [-1, -1, 1],
    [-1, 1, -1],
    [1, -1, -1],
  ],
  faces: [
    [1, 0, 2, 1],
    [0, 1, 3, 2],
    [0, 3, 2, 3],
    [1, 2, 3, 4],
  ],
  scaleFactor: 1.2,
  values: 4,
  mass: 6,
  meshSizeMultiplier: 1.6,
  invertUpside: true,
  numberValues: {
    1: 1,
    2: 3,
    3: 2,
    4: 4,
  },
  baseHeight: 0.52,
};

const d6: DieConfiguration = {
  dieType: DieTypes.d6,
  vertices: [
    [-1, -1, -1],
    [1, -1, -1],
    [1, 1, -1],
    [-1, 1, -1],
    [-1, -1, 1],
    [1, -1, 1],
    [1, 1, 1],
    [-1, 1, 1],
  ],
  faces: [
    [0, 3, 2, 1, 1],
    [1, 2, 6, 5, 2],
    [0, 1, 5, 4, 3],
    [3, 7, 6, 2, 4],
    [0, 4, 7, 3, 5],
    [4, 5, 6, 7, 6],
  ],
  scaleFactor: 0.9,
  values: 6,
  mass: 6,
  meshSizeMultiplier: 1.3,
  invertUpside: false,
  numberValues: {
    1: 4,
    2: 5,
    3: 1,
    4: 6,
    5: 2,
    6: 3,
  },
  baseHeight: 0.7,
};

const d8: DieConfiguration = {
  dieType: DieTypes.d8,
  vertices: [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1],
  ],
  faces: [
    [0, 2, 4, 1],
    [0, 4, 3, 2],
    [0, 3, 5, 3],
    [0, 5, 2, 4],
    [1, 3, 4, 5],
    [1, 4, 2, 6],
    [1, 2, 5, 7],
    [1, 5, 3, 8],
  ],
  scaleFactor: 1.6,
  values: 8,
  mass: 6.8,
  meshSizeMultiplier: 1.4,
  invertUpside: false,
  numberValues: {
    1: 4,
    2: 2,
    3: 7,
    4: 1,
    5: 8,
    6: 6,
    7: 3,
    8: 5,
  },
  baseHeight: 0.7,
};

const d10: DieConfiguration = {
  dieType: DieTypes.d10,
  vertices: [
    [1, 0, -0.105],
    [0.8090169943749475, 0.5877852522924731, 0.105],
    [0.30901699437494745, 0.9510565162951535, -0.105],
    [-0.30901699437494734, 0.9510565162951536, 0.105],
    [-0.8090169943749473, 0.5877852522924732, -0.105],
    [-1, 1.2246467991473532e-16, 0.105],
    [-0.8090169943749475, -0.587785252292473, -0.105],
    [-0.30901699437494756, -0.9510565162951535, 0.105],
    [0.30901699437494723, -0.9510565162951536, -0.105],
    [0.8090169943749473, -0.5877852522924734, 0.105],
    [0, 0, -1],
    [0, 0, 1],
  ],
  faces: [
    [5, 7, 11, 0],
    [4, 2, 10, 1],
    [1, 3, 11, 2],
    [0, 8, 10, 3],
    [7, 9, 11, 4],
    [8, 6, 10, 5],
    [9, 1, 11, 6],
    [2, 0, 10, 7],
    [3, 5, 11, 8],
    [6, 4, 10, 9],
    [1, 0, 2, -1],
    [1, 2, 3, -1],
    [3, 2, 4, -1],
    [3, 4, 5, -1],
    [5, 4, 6, -1],
    [5, 6, 7, -1],
    [7, 6, 8, -1],
    [7, 8, 9, -1],
    [9, 8, 0, -1],
    [9, 0, 1, -1],
  ],
  scaleFactor: 1.6,
  values: 10,
  mass: 7,
  meshSizeMultiplier: 1.4,
  invertUpside: false,
  numberValues: {
    0: 6,
    1: 7,
    2: 10,
    3: 5,
    4: 2,
    5: 9,
    6: 8,
    7: 3,
    8: 4,
    9: 1,
  },
  baseHeight: 0.8,
};

const d12: DieConfiguration = {
  dieType: DieTypes.d12,
  vertices: [
    [0, 0.6180339887498948, 1.618033988749895],
    [0, 0.6180339887498948, -1.618033988749895],
    [0, -0.6180339887498948, 1.618033988749895],
    [0, -0.6180339887498948, -1.618033988749895],
    [1.618033988749895, 0, 0.6180339887498948],
    [1.618033988749895, 0, -0.6180339887498948],
    [-1.618033988749895, 0, 0.6180339887498948],
    [-1.618033988749895, 0, -0.6180339887498948],
    [0.6180339887498948, 1.618033988749895, 0],
    [0.6180339887498948, -1.618033988749895, 0],
    [-0.6180339887498948, 1.618033988749895, 0],
    [-0.6180339887498948, -1.618033988749895, 0],
    [1, 1, 1],
    [1, 1, -1],
    [1, -1, 1],
    [1, -1, -1],
    [-1, 1, 1],
    [-1, 1, -1],
    [-1, -1, 1],
    [-1, -1, -1],
  ],
  faces: [
    [2, 14, 4, 12, 0, 1],
    [15, 9, 11, 19, 3, 2],
    [16, 10, 17, 7, 6, 3],
    [6, 7, 19, 11, 18, 4],
    [6, 18, 2, 0, 16, 5],
    [18, 11, 9, 14, 2, 6],
    [1, 17, 10, 8, 13, 7],
    [1, 13, 5, 15, 3, 8],
    [13, 8, 12, 4, 5, 9],
    [5, 4, 14, 9, 15, 10],
    [0, 12, 8, 10, 16, 11],
    [3, 19, 7, 17, 1, 12],
  ],
  scaleFactor: 0.9,
  values: 12,
  mass: 7,
  meshSizeMultiplier: 1.7,
  invertUpside: false,
  numberValues: {
    1: 5,
    2: 11,
    3: 8,
    4: 7,
    5: 9,
    6: 10,
    7: 2,
    8: 3,
    9: 1,
    10: 4,
    11: 6,
    12: 12,
  },
  baseHeight: 0.9,
};

const d20: DieConfiguration = {
  dieType: DieTypes.d20,
  vertices: [
    [-1, 1.618033988749895, 0],
    [1, 1.618033988749895, 0],
    [-1, -1.618033988749895, 0],
    [1, -1.618033988749895, 0],
    [0, -1, 1.618033988749895],
    [0, 1, 1.618033988749895],
    [0, -1, -1.618033988749895],
    [0, 1, -1.618033988749895],
    [1.618033988749895, 0, -1],
    [1.618033988749895, 0, 1],
    [-1.618033988749895, 0, -1],
    [-1.618033988749895, 0, 1],
  ],
  faces: [
    [0, 11, 5, 1],
    [0, 5, 1, 2],
    [0, 1, 7, 3],
    [0, 7, 10, 4],
    [0, 10, 11, 5],
    [1, 5, 9, 6],
    [5, 11, 4, 7],
    [11, 10, 2, 8],
    [10, 7, 6, 9],
    [7, 1, 8, 10],
    [3, 9, 4, 11],
    [3, 4, 2, 12],
    [3, 2, 6, 13],
    [3, 6, 8, 14],
    [3, 8, 9, 15],
    [4, 9, 5, 16],
    [2, 4, 11, 17],
    [6, 2, 10, 18],
    [8, 6, 7, 19],
    [9, 8, 1, 20],
  ],
  scaleFactor: 1,
  values: 20,
  mass: 8,
  meshSizeMultiplier: 1.7,
  invertUpside: false,
  numberValues: {
    1: 18,
    2: 4,
    3: 11,
    4: 13,
    5: 5,
    6: 14,
    7: 2,
    8: 15,
    9: 1,
    10: 9,
    11: 8,
    12: 10,
    13: 17,
    14: 3,
    15: 16,
    16: 20,
    17: 12,
    18: 7,
    19: 19,
    20: 6,
  },
  baseHeight: 1.14,
};

const d100: DieConfiguration = {
  dieType: DieTypes.d100,
  vertices: [
    [1, 0, -0.105],
    [0.8090169943749475, 0.5877852522924731, 0.105],
    [0.30901699437494745, 0.9510565162951535, -0.105],
    [-0.30901699437494734, 0.9510565162951536, 0.105],
    [-0.8090169943749473, 0.5877852522924732, -0.105],
    [-1, 1.2246467991473532e-16, 0.105],
    [-0.8090169943749475, -0.587785252292473, -0.105],
    [-0.30901699437494756, -0.9510565162951535, 0.105],
    [0.30901699437494723, -0.9510565162951536, -0.105],
    [0.8090169943749473, -0.5877852522924734, 0.105],
    [0, 0, -1],
    [0, 0, 1],
  ],
  faces: [
    [5, 7, 11, 0],
    [4, 2, 10, 1],
    [1, 3, 11, 2],
    [0, 8, 10, 3],
    [7, 9, 11, 4],
    [8, 6, 10, 5],
    [9, 1, 11, 6],
    [2, 0, 10, 7],
    [3, 5, 11, 8],
    [6, 4, 10, 9],
    [1, 0, 2, -1],
    [1, 2, 3, -1],
    [3, 2, 4, -1],
    [3, 4, 5, -1],
    [5, 4, 6, -1],
    [5, 6, 7, -1],
    [7, 6, 8, -1],
    [7, 8, 9, -1],
    [9, 8, 0, -1],
    [9, 0, 1, -1],
  ],
  scaleFactor: 1.6,
  values: 100,
  mass: 7,
  meshSizeMultiplier: 1.4,
  invertUpside: false,
  numberValues: {
    0: 60,
    1: 70,
    2: 0,
    3: 50,
    4: 20,
    5: 90,
    6: 80,
    7: 30,
    8: 40,
    9: 10,
  },
  baseHeight: 0.8,
};

export const dieConfigurations = {
  [DieTypes.d4]: d4,
  [DieTypes.d6]: d6,
  [DieTypes.d8]: d8,
  [DieTypes.d10]: d10,
  [DieTypes.d12]: d12,
  [DieTypes.d20]: d20,
  [DieTypes.d100]: d100,
};

export const customSetIdPrefix = "customSet";
export const customSetManifestFileName = "manifest.json";
